import { getGeolocation } from "@helpers/backend"
import { safeSessionStorage } from "@helpers/storage"
import { getUser } from "@helpers/supabase"

// NOTE: Only caches. Everything here comes back from the server (geolocation)
// or the Supabase session when it's missing, e.g. after a full page load with
// storage blocked. The password is never kept: the account is created on the
// step that asks for it.
const CITY_KEY = "city"
const COUNTRY_CODE_KEY = "countrycode"
const CURRENCY_KEY = "currency"
const EMAIL_KEY = "email"
const ID_KEY = "id"
const SIGN_UP_EMAIL_KEY = "sign_up_email"
const ZIP_CODE_KEY = "zipcode"

export function getSignUpEmailFromSessionStorage(): string | null {
  return safeSessionStorage.getItem(SIGN_UP_EMAIL_KEY)
}

export function setSignUpEmailInSessionStorage(email: string): void {
  safeSessionStorage.setItem(SIGN_UP_EMAIL_KEY, email)
}

export async function getUserCity(): Promise<string> {
  const cityFromSessionStorage = safeSessionStorage.getItem(CITY_KEY)

  if (cityFromSessionStorage) return cityFromSessionStorage

  const geolocation = await getAndStoreGeolocation()

  return geolocation?.city
}

export async function getUserCountryCode(): Promise<string> {
  const countryFromSessionStorage = safeSessionStorage.getItem(COUNTRY_CODE_KEY)

  if (countryFromSessionStorage) return countryFromSessionStorage

  const geolocation = await getAndStoreGeolocation()

  return geolocation?.country
}

export async function getUserCurrency(): Promise<string> {
  const currencyFromSessionStorage = safeSessionStorage.getItem(CURRENCY_KEY)

  if (currencyFromSessionStorage) return currencyFromSessionStorage

  const geolocation = await getAndStoreGeolocation()

  return geolocation?.currency || "eur"
}

export async function getUserEmail(): Promise<string | undefined> {
  const emailFromSessionStorage = safeSessionStorage.getItem(EMAIL_KEY)

  if (emailFromSessionStorage) return emailFromSessionStorage

  const user = await getAndStoreUser()

  return user?.email
}

export async function getUserId(): Promise<string | undefined> {
  const idFromSessionStorage = safeSessionStorage.getItem(ID_KEY)

  if (idFromSessionStorage) return idFromSessionStorage

  const user = await getAndStoreUser()

  return user?.id
}

export async function getUserZipCode(): Promise<string> {
  const zipCodeFromSessionStorage = safeSessionStorage.getItem(ZIP_CODE_KEY)

  if (zipCodeFromSessionStorage) return zipCodeFromSessionStorage

  const geolocation = await getAndStoreGeolocation()

  return geolocation?.zipcode
}

function storeIfSet(key: string, value: string | undefined | null) {
  // NOTE: Not `String(undefined)`, which would be cached as "undefined".
  if (value) safeSessionStorage.setItem(key, value)
}

async function getAndStoreGeolocation() {
  const geolocation = await getGeolocation()

  storeIfSet(CITY_KEY, geolocation?.city)
  storeIfSet(COUNTRY_CODE_KEY, geolocation?.country)
  storeIfSet(CURRENCY_KEY, geolocation?.currency)
  storeIfSet(ZIP_CODE_KEY, geolocation?.zipcode)

  return geolocation
}

async function getAndStoreUser() {
  const { data } = await getUser()

  const user = data.user

  storeIfSet(EMAIL_KEY, user?.email)
  storeIfSet(ID_KEY, user?.id)

  return user
}
